import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { selectCurrentClient } from '../../CommonSelectors/CommonAreaSelector';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { ManualAccountsAssetAllocationDTO } from '../../ManualAccountsAssetAllocation/types';
import { queryKeys } from './queryKeys';
import { RiskToleranceQuestionnaireType } from '../../CommonSelectors/CommonArea.types';
import { CMAType } from '../../AdviserSettings/Page/CustomModelAssumptions/types';
import {
  AccountDistributionStrategyIdDTO,
  CurrentDistributionOrderDTO,
} from '../../AccountDistributionOrderModal/api/useAccountDistributionOptionsQuery';

export interface CashflowEnginesOptionDTO {
  label: string;
  value: number;
}

interface EmployerOptionDTO {
  label: string;
  value: number;
}

interface OptionsDTO {
  CashflowEngines: CashflowEnginesOptionDTO[];
  EmployerOptions: EmployerOptionDTO[];
}

export enum IntegratedSystemType {
  OrionConnect = 'Orion Connect',
  Ally = 'Ally',
}

export interface IntegratedSystemsDTO {
  Id: string;
  Name: IntegratedSystemType;
}

export enum PlanStartYearDTO {
  CurrentYear = 0,
  NextYear = 1,
}

export interface SettingsDTO {
  Inflation: number;
  SocialSecurityInflation: number;
  EducationInflation: number;
  LoanBalanceInterest: number;
  PrincipalName: string;
  LifeExpectancyPrincipal: number;
  SpouseName: string;
  LifeExpectancySpouse: number;
  AdvisersAssumptionsEnabled: boolean;
  DisplayAssetBucketing: true;
  AllowAssetBucketing: null | boolean;
  EmployerId: null | number;
  CashflowEngine: number;
  IntegratedSystems: IntegratedSystemsDTO[];
  SocialSecurityUseSpousalBenefit: boolean;
  SocialSecurityFileForSurvivorBenefits: boolean;
  PortfolioReturnsMethodology: null | number;
  InheritedManagedAccountGrouping: null | boolean;
  ShowCashflowEngineVersionSelector: boolean;
  AllowBeFi20: boolean | null;
  DisplayBeFi20: boolean;
  RiskToleranceWorkflow: RiskToleranceQuestionnaireType | null;
  Is3dRiskToleranceWorkflowEnabled: boolean;
  CapitalMarketAssumptions: CMAType | null;
  ShowCmaCustomAssumptions: boolean;
  AllowPulseCheck: boolean | null;
  DisplayPulseCheck: boolean;
  PlanStartYear: PlanStartYearDTO | null;
  ExcessCashFlowPercentToSave: number | null;
  ManualAccountsAssetAllocation: ManualAccountsAssetAllocationDTO;
  MonteCarloSuccessRange: number | null;
  AccountDistributionStrategy: AccountDistributionStrategyIdDTO;
  AccountDistributionOrder: CurrentDistributionOrderDTO | null;
}

interface ClientSettingsDTO {
  Options: OptionsDTO;
  Settings: SettingsDTO;
}

interface FetchClientSettingsParams {
  clientId: number;
}

const fetchClientSettings = async ({
  clientId,
}: FetchClientSettingsParams): Promise<ClientSettingsDTO> => {
  const response = await authFetch(urlWithParams('/Consumer/ClientSettings', { clientId }));

  return {
    ...response,
    Settings: {
      ...response.Settings,
      AccountDistributionOrder: response.Settings.AccountDistributionOrder
        ? {
            preRetirement: response.Settings.AccountDistributionOrder.PreRetirement,
            postRetirement: response.Settings.AccountDistributionOrder.PostRetirement,
          }
        : null,
      ManualAccountsAssetAllocation: response.Settings.ManualAccountsAssetAllocation && {
        strategy: response.Settings.ManualAccountsAssetAllocation.Strategy,
        details: response.Settings.ManualAccountsAssetAllocation.Details,
      },
    },
  };
};

export const useClientSettingsQuery = () => {
  const { Id: clientId } = useSelector(selectCurrentClient);

  return useQuery(
    queryKeys.clientSettings(clientId),

    () => fetchClientSettings({ clientId }),

    {
      cacheTime: 0,
    },
  );
};
